import React, { useState, useMemo } from "react";
import { Trophy, Receipt, AlertCircle, TrendingUp, Calendar, Sparkles, Medal, Award, ChevronDown, ChevronUp, Truck, Wallet, CheckCircle2, Users } from "lucide-react";

interface StaffData {
  netSales: number;
  staffId: string;
  staffName: string;
  totalDeliveryFee: number;
  totalDiscount: number;
  totalOrders: number;
  totalSales: number;
  totalReceived: number;
  totalOutstanding: number;
  latestOrderDate: string;
}

interface Props {
  staffData: StaffData[];
}

// Helper Utilities
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-QA", {
    style: "currency",
    currency: "QAR",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const getInitials = (name: string) =>
  name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "ST";

const formatDate = (dateStr: string) => {
  if (!dateStr) return "N/A";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

// Reusable UI Components
const StatBadge: React.FC<{
  icon: React.ElementType;
  label: string;
  value: string | number;
  className?: string;
  iconClass?: string;
  labelClass?: string;
  valueClass?: string;
}> = ({ icon: Icon, label, value, className = "bg-slate-50 border-slate-300", iconClass = "text-slate-500", labelClass = "text-slate-400", valueClass = "text-slate-800" }) => (
  <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${className}`}>
    <Icon className={`w-5 h-5 ${iconClass}`} />
    <div>
      <span className={`text-[11px] uppercase font-semibold block leading-tight ${labelClass}`}>{label}</span>
      <span className={`text-base font-bold ${valueClass}`}>{value}</span>
    </div>
  </div>
);

const StatCell: React.FC<{
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}> = ({ label, value, valueClass = "text-slate-800" }) => (
  <div>
    <div className="text-xs text-slate-400 font-medium uppercase tracking-wider">{label}</div>
    <div className={`font-bold text-base ${valueClass}`}>{value}</div>
  </div>
);

const ExpandedStatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ElementType;
  iconClass?: string;
  children?: React.ReactNode;
}> = ({ label, value, icon: Icon, iconClass = "text-emerald-600", children }) => (
  <div className="p-4 rounded-xl bg-gray-100 border border-slate-300 shadow-sm">
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm font-semibold text-slate-400 uppercase tracking-wider">{label}</span>
      <Icon className={`w-5 h-5 ${iconClass}`} />
    </div>
    <div className="text-xl font-bold text-slate-900">{value}</div>
    {children}
  </div>
);

const RankBadge: React.FC<{ index: number }> = ({ index }) => {
  const icons = [Trophy, Medal, Award];

  if (index < 3) {
    const rankClasses = ["bg-amber-50 border-amber-200/80 text-amber-600", "bg-slate-100 border-slate-200 text-slate-600", "bg-orange-50 border-orange-200/80 text-amber-700"];
    const Icon = icons[index];
    return (
      <div className={`flex items-center justify-center w-10 h-10 rounded-full border shadow-sm ${rankClasses[index]}`}>
        <Icon className="w-5 h-5" />
      </div>
    );
  }

  return <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 text-slate-500 font-bold text-base border border-slate-200">#{index + 1}</div>;
};

// Staff Card Component
const StaffCard: React.FC<{
  staff: StaffData;
  index: number;
  highestSales: number;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ staff, index, highestSales, isExpanded, onToggle }) => {
  const performancePct = Math.min(100, Math.max(0, ((staff.totalSales || 0) / highestSales) * 100));

  return (
    <div className="rounded-xl border border-slate-300 bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      <div onClick={onToggle} className="p-5 cursor-pointer hover:bg-slate-50/50 transition-colors">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <RankBadge index={index} />
            </div>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 border-2 border-indigo-300 text-indigo-700 font-bold text-base">
                {getInitials(staff.staffName)}
              </div>
              <div className="min-w-0 font-semibold text-slate-900 text-base truncate">{staff.staffName || "Unknown Staff"}</div>
            </div>
          </div>

          <div className="flex items-center gap-6 flex-wrap">
            <StatCell label="Orders" value={staff.totalOrders || 0} />
            <StatCell label="Net Sales" value={formatCurrency(staff.netSales)} valueClass="text-emerald-600" />
            <StatCell
              label="Outstanding"
              value={
                (staff.totalOutstanding || 0) > 0 ? (
                  <span className="text-rose-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {formatCurrency(staff.totalOutstanding)}
                  </span>
                ) : (
                  <span className="text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Clear
                  </span>
                )
              }
            />
          </div>

          <button className="p-2 rounded-lg hover:bg-slate-200/60 text-slate-400 hover:text-slate-700 transition flex-shrink-0">
            {isExpanded ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
          <StatCell label="Total Received" value={formatCurrency(staff.totalReceived)} valueClass="text-indigo-600" />
          <StatCell label="Total Discounts" value={formatCurrency(staff.totalDiscount)} valueClass="text-amber-600" />
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Last Order</div>
            <div className="font-medium text-slate-600 text-sm flex items-center gap-1.5 mt-0.5">
              <Calendar className="w-4 h-4 text-slate-400" />
              {formatDate(staff.latestOrderDate)}
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t-2 border-indigo-100 bg-gradient-to-b from-indigo-50/50 to-white p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ExpandedStatCard label="Total Sales" value={formatCurrency(staff.totalSales)} icon={Wallet} />
            <ExpandedStatCard label="Total Received" value={formatCurrency(staff.totalReceived)} icon={Wallet} />
            <ExpandedStatCard label="Delivery Fees" value={formatCurrency(staff.totalDeliveryFee)} icon={Truck} iconClass="text-blue-600" />
            <ExpandedStatCard label="Performance" value={`${performancePct.toFixed(1)}%`} icon={TrendingUp} iconClass="text-indigo-600">
              <div className="w-full bg-slate-100 rounded-full h-2 mt-2 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-full rounded-full transition-all duration-700" style={{ width: `${performancePct}%` }} />
              </div>
            </ExpandedStatCard>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const StaffPerformance: React.FC<Props> = ({ staffData = [] }) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const sortedStaff = useMemo(() => {
    return Array.isArray(staffData) ? [...staffData].sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0)) : [];
  }, [staffData]);

  const highestSales = useMemo(() => Math.max(...sortedStaff.map((s) => s.totalSales || 0), 1), [sortedStaff]);

  const { totalTeamSales, totalTeamOrders } = useMemo(() => {
    return sortedStaff.reduce(
      (acc, s) => ({
        totalTeamSales: acc.totalTeamSales + (s.totalSales || 0),
        totalTeamOrders: acc.totalTeamOrders + (s.totalOrders || 0),
      }),
      { totalTeamSales: 0, totalTeamOrders: 0 },
    );
  }, [sortedStaff]);

  const toggleRow = (staffId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      newSet.has(staffId) ? newSet.delete(staffId) : newSet.add(staffId);
      return newSet;
    });
  };

  if (!sortedStaff.length) {
    return (
      <div className="w-full rounded-2xl border border-slate-200 bg-white p-12 text-center">
        <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 text-lg">No staff data available.</p>
      </div>
    );
  }

  return (
    <div className="relative w-full rounded-2xl border border-slate-200/80 bg-white shadow-lg transition-all duration-300">
      {/* Header Section */}
      <div className="p-6 border-b border-slate-100">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-md">
              <Sparkles className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-xl font-bold text-slate-900 tracking-tight">Staff Performance</h2>
              <p className="text-sm text-slate-500 mt-0.5">{sortedStaff.length} Active Staff Members</p>
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <StatBadge icon={Users} label="Total Staff" value={sortedStaff.length} />
            <StatBadge icon={Receipt} label="Total Orders" value={totalTeamOrders.toLocaleString()} />
            <StatBadge
              icon={TrendingUp}
              label="Total Sales"
              value={formatCurrency(totalTeamSales)}
              className="bg-gradient-to-br from-indigo-50 to-indigo-100/60 border-indigo-300"
              iconClass="text-indigo-600"
              labelClass="text-indigo-600"
              valueClass="text-indigo-900"
            />
          </div>
        </div>
      </div>

      {/* Vertical Card Layout */}
      <div className="p-4 space-y-3">
        {sortedStaff.map((staff, index) => (
          <StaffCard key={staff.staffId || index} staff={staff} index={index} highestSales={highestSales} isExpanded={expandedRows.has(staff.staffId)} onToggle={() => toggleRow(staff.staffId)} />
        ))}
      </div>

      {/* Footer */}
      <div className="px-6 py-3.5 bg-slate-50/80 border-t border-slate-100 rounded-b-2xl flex items-center justify-between text-sm text-slate-500">
        <span>
          Showing <strong className="text-slate-700">{sortedStaff.length}</strong> staff entries
        </span>
        <span className="text-xs text-slate-400">Click on any card to expand details</span>
      </div>
    </div>
  );
};

export default StaffPerformance;
